import { useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { FormError, FormField, FormInput } from '@/components/form';
import { Button } from '@/components/ui/button';

import type { Webhook } from '@/lib/api/webhooks.api';

type DataType = 'availability_data' | 'lead_data' | 'shift_data';

const DATA_TYPE_OPTIONS: { value: DataType; label: string }[] = [
  { value: 'availability_data', label: 'Availability Data' },
  { value: 'lead_data', label: 'Lead Data' },
  { value: 'shift_data', label: 'Shift Data' },
];

interface TriggerWebhookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: {
    dataType: DataType;
    fromDate: string;
    toDate: string;
    confirmedOnly?: boolean;
  }) => Promise<void>;
  isPending: boolean;
  webhook: Webhook | null;
}

export function TriggerWebhookDialog({
  open,
  onOpenChange,
  onSubmit,
  isPending,
  webhook,
}: TriggerWebhookDialogProps) {
  const [dataType, setDataType] = useState<DataType>('availability_data');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [confirmedOnly, setConfirmedOnly] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async () => {
    setFormError('');

    if (!fromDate) {
      setFormError('From date is required');
      return;
    }

    if (!toDate) {
      setFormError('To date is required');
      return;
    }

    if (new Date(toDate) < new Date(fromDate)) {
      setFormError('To date must be on or after From date');
      return;
    }

    try {
      await onSubmit({
        dataType,
        fromDate,
        toDate,
        ...(dataType === 'availability_data' && confirmedOnly ? { confirmedOnly: true } : {}),
      });
      setFromDate('');
      setToDate('');
      setDataType('availability_data');
      setConfirmedOnly(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      setFormError(err?.response?.data?.message ?? 'Failed to trigger webhook.');
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 bg-black/50" />
        <Dialog.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed top-[50%] left-[50%] z-50 max-h-[90vh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] overflow-y-auto rounded-lg bg-white p-6 shadow-lg dark:bg-slate-900">
          <Dialog.Title className="mb-2 text-lg font-semibold">Send Data</Dialog.Title>
          <Dialog.Description className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Send calls data to &ldquo;{webhook?.name}&rdquo;. Select a date range and the type of
            data to include.
          </Dialog.Description>

          <div className="space-y-4">
            <div>
              <label
                htmlFor="trigger-data-type"
                className="text-foreground mb-1 block text-sm font-medium"
              >
                Data Type <span className="text-destructive">*</span>
              </label>
              <select
                id="trigger-data-type"
                value={dataType}
                onChange={(e) => setDataType(e.target.value as DataType)}
                className="bg-background border-input w-full rounded-md border px-3 py-2 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              >
                {DATA_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <p className="text-muted-foreground mt-1 text-xs">
                Only calls that have this data populated will be included.
              </p>
            </div>

            {dataType === 'availability_data' && (
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={confirmedOnly}
                  onChange={(e) => setConfirmedOnly(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-800"
                />
                <span className="font-medium">Confirmed only</span>
                <span className="text-muted-foreground">
                  - Only include calls where availability is confirmed
                </span>
              </label>
            )}

            <FormField label="From Date" htmlFor="trigger-from-date" required error={undefined}>
              <FormInput
                id="trigger-from-date"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </FormField>

            <FormField label="To Date" htmlFor="trigger-to-date" required error={undefined}>
              <FormInput
                id="trigger-to-date"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </FormField>

            {formError && <FormError message={formError} />}
          </div>

          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? 'Sending...' : 'Send'}
            </Button>
          </div>

          <Dialog.Close asChild>
            <button
              className="ring-offset-background focus:ring-ring absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
